import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { drawCertificate, loadImage, unitToMm, FONTS, makeQrImage, qrTextForRow, QR_KEY } from "@/lib/idcRender";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Plus, Save, Trash2, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Circle, Square, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function TemplateDesigner({ template, onClose }) {
  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState(template.dataset_id || "");
  const [dataset, setDataset] = useState(null);
  const [layout, setLayout] = useState(template.layout || []);
  const [selected, setSelected] = useState(null);
  const [bgImg, setBgImg] = useState(null);
  const [qrImg, setQrImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);

  const cw = unitToMm(template.width, template.unit);
  const ch = unitToMm(template.height, template.unit);

  useEffect(() => { api.get("/idc/datasets").then((r) => setDatasets(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (template.image) loadImage(template.image).then(setBgImg).catch(() => {}); }, [template.image]);

  useEffect(() => {
    if (!datasetId) { setDataset(null); return; }
    api.get(`/idc/datasets/${datasetId}`).then((r) => setDataset(r.data)).catch(() => {});
  }, [datasetId]);

  const fields = dataset?.fields || [];
  const sampleRow = dataset?.rows?.[0] || {};
  const hasQR = layout.some((l) => l.type === "qr");

  useEffect(() => {
    if (!hasQR) { setQrImg(null); return; }
    const text = fields.length ? qrTextForRow(sampleRow, fields) : "QR CODE";
    let alive = true;
    makeQrImage(text, 300).then((img) => { if (alive) setQrImg(img); }).catch(() => {});
    return () => { alive = false; };
  }, [hasQR, dataset]); // eslint-disable-line react-hooks/exhaustive-deps

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dispW = Math.min(wrap.clientWidth, 720);
    const dispH = Math.round((dispW * ch) / cw);
    canvas.width = dispW;
    canvas.height = dispH;
    canvas.style.width = dispW + "px";
    canvas.style.height = dispH + "px";
    drawCertificate(canvas, template, bgImg, layout, sampleRow, qrImg ? { [QR_KEY]: qrImg } : {}, { selectedKey: selected, placeholders: true });
  }, [bgImg, layout, selected, sampleRow, template, cw, ch, qrImg]);

  useEffect(() => { render(); }, [render]);
  useEffect(() => {
    const ro = new ResizeObserver(render);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [render]);

  const addField = (f) => {
    if (layout.find((l) => l.key === f.key)) { setSelected(f.key); return; }
    const base = { key: f.key, label: f.label, type: f.type, x: 0.35, y: 0.4 };
    const item = f.type === "photo"
      ? { ...base, w: 0.22, h: 0.4, shape: "square" }
      : { ...base, w: 0.4, fontSize: 0.06, color: "#111827", align: "left", bold: false, italic: false, fontFamily: "Helvetica" };
    setLayout([...layout, item]);
    setSelected(f.key);
  };

  const addQR = () => {
    if (layout.find((l) => l.key === QR_KEY)) { setSelected(QR_KEY); return; }
    setLayout([...layout, { key: QR_KEY, label: "QR Code", type: "qr", x: 0.68, y: 0.55, w: 0.2, h: 0.35 }]);
    setSelected(QR_KEY);
  };

  const updateSel = (patch) => setLayout(layout.map((l) => (l.key === selected ? { ...l, ...patch } : l)));
  const removeSel = () => { setLayout(layout.filter((l) => l.key !== selected)); setSelected(null); };

  const hitTest = (fx, fy) => {
    const W = canvasRef.current.width, H = canvasRef.current.height;
    for (let i = layout.length - 1; i >= 0; i--) {
      const l = layout[i];
      const x = l.x * W, y = l.y * H;
      const w = (l.w || 0.3) * W;
      const h = (l.type === "photo" || l.type === "qr") ? (l.h || 0.3) * H : (l.h ? l.h * H : (l.fontSize || 0.05) * W * 1.3);
      if (fx >= x && fx <= x + w && fy >= y && fy <= y + h) return l.key;
    }
    return null;
  };

  const pointerDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (canvasRef.current.width / rect.width);
    const py = (e.clientY - rect.top) * (canvasRef.current.height / rect.height);
    const key = hitTest(px, py);
    setSelected(key);
    if (key) {
      const l = layout.find((x) => x.key === key);
      dragRef.current = { key, offX: px / canvasRef.current.width - l.x, offY: py / canvasRef.current.height - l.y };
    }
  };
  const pointerMove = (e) => {
    if (!dragRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (canvasRef.current.width / rect.width);
    const py = (e.clientY - rect.top) * (canvasRef.current.height / rect.height);
    const nx = Math.max(0, Math.min(1, px / canvasRef.current.width - dragRef.current.offX));
    const ny = Math.max(0, Math.min(1, py / canvasRef.current.height - dragRef.current.offY));
    setLayout((prev) => prev.map((l) => (l.key === dragRef.current.key ? { ...l, x: nx, y: ny } : l)));
  };
  const pointerUp = () => { dragRef.current = null; };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/idc/templates/${template.id}`, {
        name: template.name, width: template.width, height: template.height,
        unit: template.unit, orientation: template.orientation, layout, dataset_id: datasetId, image: "",
      });
      toast.success("Design saved");
      onClose();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const sel = layout.find((l) => l.key === selected);

  return (
    <div className="space-y-4" data-testid="template-designer">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>← Back</Button>
        <p className="font-heading font-semibold text-sm">{template.name} — Design</p>
        <div className="flex-1" />
        <Select value={datasetId} onValueChange={setDatasetId}>
          <SelectTrigger className="w-52" data-testid="designer-dataset-select"><SelectValue placeholder="Select data file (for fields)" /></SelectTrigger>
          <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={save} disabled={saving} data-testid="designer-save-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Design"}
        </Button>
      </div>

      {!datasetId && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-3 text-sm">
          To place fields on the template, first select a Data file above.
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_300px] gap-4">
        <div className="bg-slate-100 border border-border rounded-xl p-3">
          <div ref={wrapRef} className="w-full flex justify-center">
            <canvas
              ref={canvasRef}
              data-testid="designer-canvas"
              onPointerDown={pointerDown}
              onPointerMove={pointerMove}
              onPointerUp={pointerUp}
              onPointerLeave={pointerUp}
              className="bg-white rounded shadow touch-none cursor-move max-w-full"
            />
          </div>
          {!bgImg && <p className="text-xs text-cyan-600 mt-2 text-center animate-pulse">Loading template image...</p>}
          <p className="text-xs text-muted-foreground mt-2 text-center">Drag a field to reposition it. Tap to select.</p>
        </div>

        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Add Fields</p>
            <div className="flex flex-wrap gap-1.5">
              {fields.map((f) => {
                const added = layout.some((l) => l.key === f.key);
                return (
                  <button key={f.key} onClick={() => addField(f)} data-testid={`designer-addfield-${f.key}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${added ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-cyan-50 text-cyan-800 border-cyan-200 hover:bg-cyan-100"}`}>
                    <Plus className="h-3 w-3" /> {f.label}
                  </button>
                );
              })}
              {(() => {
                const qrAdded = layout.some((l) => l.key === QR_KEY);
                return (
                  <button onClick={addQR} data-testid="designer-addfield-qr"
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${qrAdded ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100"}`}>
                    <QrCode className="h-3 w-3" /> QR Code
                  </button>
                );
              })()}
              {fields.length === 0 && <p className="text-xs text-muted-foreground">No fields. Select a data file.</p>}
            </div>
            {hasQR && <p className="text-[11px] text-muted-foreground mt-2">QR Code encodes the full row data (all fields) — scanning it on a generated ID shows that record's details.</p>}
          </div>

          {sel && (
            <div className="bg-card border border-border rounded-xl p-3 space-y-3" data-testid="designer-style-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{sel.label}</p>
                <button onClick={removeSel} className="text-red-500 hover:text-red-600" data-testid="designer-remove-field"><Trash2 className="h-4 w-4" /></button>
              </div>

              {sel.type === "photo" || sel.type === "qr" ? (
                <>
                  <div>
                    <Label className="text-xs">Width</Label>
                    <Slider min={5} max={100} value={[Math.round(sel.w * 100)]} onValueChange={(v) => updateSel({ w: v[0] / 100 })} data-testid="photo-width-slider" />
                  </div>
                  <div>
                    <Label className="text-xs">Height</Label>
                    <Slider min={5} max={100} value={[Math.round(sel.h * 100)]} onValueChange={(v) => updateSel({ h: v[0] / 100 })} data-testid="photo-height-slider" />
                  </div>
                  {sel.type === "photo" && (
                  <div>
                    <Label className="text-xs">Shape</Label>
                    <div className="flex gap-2 mt-1">
                      <Button size="sm" variant={sel.shape === "square" ? "default" : "outline"} onClick={() => updateSel({ shape: "square" })} className="gap-1 h-8"><Square className="h-3.5 w-3.5" /> Square</Button>
                      <Button size="sm" variant={sel.shape === "round" ? "default" : "outline"} onClick={() => updateSel({ shape: "round" })} className="gap-1 h-8"><Circle className="h-3.5 w-3.5" /> Round</Button>
                    </div>
                  </div>
                  )}
                  {sel.type === "photo" && sel.shape !== "round" && (
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Corner Round</Label>
                        <span className="text-xs font-bold text-cyan-700 tabular-nums" data-testid="photo-radius-value">{Math.round((sel.radius || 0) * 100)}%</span>
                      </div>
                      <Slider min={0} max={50} value={[Math.round((sel.radius || 0) * 100)]} onValueChange={(v) => updateSel({ radius: v[0] / 100 })} data-testid="photo-radius-slider" />
                    </div>
                  )}
                  {sel.type === "photo" && (
                    <>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Border Width</Label>
                          <span className="text-xs font-bold text-cyan-700 tabular-nums" data-testid="photo-border-width-value">{((sel.borderW || 0) * cw).toFixed(1)} px</span>
                        </div>
                        <Slider min={0} max={20} step={0.5} value={[(sel.borderW || 0) * 100]} onValueChange={(v) => updateSel({ borderW: v[0] / 100 })} data-testid="photo-border-width-slider" />
                      </div>
                      <div>
                        <Label className="text-xs">Border Color</Label>
                        <input type="color" value={sel.borderColor || "#111827"} onChange={(e) => updateSel({ borderColor: e.target.value })} className="w-full h-8 rounded border cursor-pointer" data-testid="photo-border-color-input" />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Font Size</Label>
                      <span className="text-xs font-bold text-cyan-700 tabular-nums" data-testid="font-size-value">
                        {Math.round(sel.fontSize * cw * 2.8346)} pt
                      </span>
                    </div>
                    <Slider min={2} max={20} step={0.5} value={[sel.fontSize * 100]} onValueChange={(v) => updateSel({ fontSize: v[0] / 100 })} data-testid="font-size-slider" />
                  </div>
                  <div>
                    <Label className="text-xs">Box Width</Label>
                    <Slider min={10} max={100} value={[Math.round(sel.w * 100)]} onValueChange={(v) => updateSel({ w: v[0] / 100 })} data-testid="box-width-slider" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Box Height</Label>
                      <span className="text-[10px] text-muted-foreground">long text wraps to next line</span>
                    </div>
                    <Slider min={5} max={80} value={[Math.round((sel.h || 0.12) * 100)]} onValueChange={(v) => updateSel({ h: v[0] / 100 })} data-testid="box-height-slider" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Color</Label>
                      <input type="color" value={sel.color} onChange={(e) => updateSel({ color: e.target.value })} className="w-full h-8 rounded border cursor-pointer" data-testid="font-color-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Font</Label>
                    <Select value={sel.fontFamily} onValueChange={(v) => updateSel({ fontFamily: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid="font-family-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant={sel.bold ? "default" : "outline"} onClick={() => updateSel({ bold: !sel.bold })} className="h-8 w-8 p-0" data-testid="bold-btn"><Bold className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={sel.italic ? "default" : "outline"} onClick={() => updateSel({ italic: !sel.italic })} className="h-8 w-8 p-0" data-testid="italic-btn"><Italic className="h-3.5 w-3.5" /></Button>
                    <div className="flex-1" />
                    <Button size="sm" variant={sel.align === "left" ? "default" : "outline"} onClick={() => updateSel({ align: "left" })} className="h-8 w-8 p-0"><AlignLeft className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={sel.align === "center" ? "default" : "outline"} onClick={() => updateSel({ align: "center" })} className="h-8 w-8 p-0"><AlignCenter className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={sel.align === "right" ? "default" : "outline"} onClick={() => updateSel({ align: "right" })} className="h-8 w-8 p-0"><AlignRight className="h-3.5 w-3.5" /></Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
